// frontend/src/components/video/SegmentStrip.tsx
import { AlertCircle, Play } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatDurationClock } from "@/utils/duration";
import { formatWindow } from "@/utils/timeFormat";
import type { VideoFile } from "@/types";

interface SegmentStripProps {
  segments: VideoFile[];
  activeIndex: number;
  failedIds: string[];
  onSelect: (index: number) => void;
}

/**
 * Horizontal strip of per-segment cards under the playlist player: thumbnail,
 * "Segment N", duration and recorded-at clock time (so gaps between stream
 * attempts are visible). Clicking a card jumps to that segment.
 */
export default function SegmentStrip({
  segments,
  activeIndex,
  failedIds,
  onSelect,
}: SegmentStripProps) {
  const { t } = useTranslation(["pages"]);

  return (
    <div className="mt-3 flex gap-2 overflow-x-auto pb-1" data-testid="segment-strip">
      {segments.map((segment, index) => {
        const isActive = index === activeIndex;
        const failed = failedIds.includes(segment.id);
        return (
          <button
            key={segment.id}
            type="button"
            onClick={() => onSelect(index)}
            aria-current={isActive || undefined}
            className={`w-28 shrink-0 rounded-lg border p-1.5 text-left transition-colors ${
              isActive
                ? "border-primary-600 bg-primary-50 dark:bg-primary-900/30"
                : "border-secondary-200 hover:border-secondary-400"
            }`}
          >
            <div className="relative aspect-video w-full overflow-hidden rounded bg-secondary-100 dark:bg-secondary-800">
              {segment.thumbnailUrl ? (
                <img
                  src={segment.thumbnailUrl}
                  alt=""
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover"
                />
              ) : (
                <span className="absolute inset-0 flex items-center justify-center text-secondary-400">
                  <Play
                    className="h-4 w-4"
                    fill="currentColor"
                    aria-hidden="true"
                  />
                </span>
              )}
              {failed && (
                <AlertCircle
                  className="absolute right-1 top-1 h-4 w-4 text-red-500"
                  role="img"
                  aria-label={t("pages:sessions.videoPlayer.segmentFailed")}
                />
              )}
            </div>
            <div className="mt-1 text-xs font-semibold">
              {t("pages:sessions.videoPlayer.segmentLabel", {
                index: index + 1,
              })}
            </div>
            <div className="text-[11px] tabular-nums text-secondary-500">
              <span>
                {segment.duration != null
                  ? formatDurationClock(segment.duration)
                  : "—"}
              </span>
              {segment.recordedAt
                ? ` · ${formatWindow(segment.recordedAt, null)}`
                : ""}
            </div>
          </button>
        );
      })}
    </div>
  );
}
